import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PenLine, Check, X, RotateCcw, FileText, ChevronRight, Clock } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Canvas de signature ───────────────────────────────────────────────────────

function SignatureCanvas({ onValidate, onCancel, signaireNom, onChangeSignataire, contrat }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [hasDrawing, setHasDrawing] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const pos = getPos(e, canvasRef.current);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1, 0, Math.PI * 2);
    ctx.fillStyle = '#1e3a5f';
    ctx.fill();
    setHasDrawing(true);
  };

  const draw = (e) => {
    if (!drawing.current || !canvasRef.current) return;
    e.preventDefault();
    const pos = getPos(e, canvasRef.current);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { drawing.current = false; };

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasDrawing(false);
  };

  const handleValidate = () => {
    if (!hasDrawing) { toast.error('Veuillez signer avant de valider'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onValidate(dataUrl);
  };

  return (
    <div className="flex flex-col h-full" style={{ background: '#F8FAFF' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#6B7280' }}>
            Contrat {contrat?.numero}
          </p>
          <h2 className="text-xl font-bold mt-0.5" style={{ color: '#1e3a5f' }}>
            {contrat?.client_nom || '—'}
          </h2>
        </div>
        <button onClick={onCancel} className="p-2 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <X size={20} style={{ color: '#6B7280' }} />
        </button>
      </div>

      {/* Signataire */}
      <div className="flex items-center gap-3 px-6 py-3 border-b" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        <span className="text-sm font-semibold" style={{ color: '#374151' }}>Signataire :</span>
        <input
          value={signaireNom}
          onChange={e => onChangeSignataire(e.target.value)}
          placeholder="Nom du signataire"
          className="flex-1 px-3 py-2 rounded-xl border text-sm font-semibold"
          style={{ borderColor: '#CBD5E1', color: '#1e3a5f' }}
        />
      </div>

      {/* Instructions */}
      <div className="px-6 py-2 flex items-center justify-between">
        <p className="text-xs" style={{ color: '#9CA3AF' }}>
          Signez dans le cadre ci-dessous
        </p>
        <button onClick={clearCanvas} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
          style={{ background: '#FEF3C7', color: '#92400E' }}>
          <RotateCcw size={12} /> Effacer
        </button>
      </div>

      {/* Canvas */}
      <div className="flex-1 px-4 pb-2" style={{ minHeight: 0 }}>
        <canvas
          ref={canvasRef}
          width={900}
          height={360}
          className="w-full h-full rounded-2xl border-2"
          style={{ borderColor: '#CBD5E1', background: 'white', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
      </div>

      {/* Boutons */}
      <div className="flex gap-3 px-4 py-4">
        <button onClick={handleValidate}
          disabled={!hasDrawing || !signaireNom.trim()}
          className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold text-white disabled:opacity-40"
          style={{ background: hasDrawing && signaireNom.trim() ? '#22C55E' : '#9CA3AF', fontSize: '1rem' }}>
          <Check size={20} /> Valider la signature
        </button>
        <button onClick={onCancel}
          className="px-5 py-4 rounded-2xl border-2 font-semibold text-base"
          style={{ borderColor: '#E2E8F0', color: '#6B7280' }}>
          Retour
        </button>
      </div>
    </div>
  );
}

// ─── Carte contrat ─────────────────────────────────────────────────────────────

function ContratCard({ contrat, onSign }) {
  return (
    <div
      onClick={onSign}
      className="flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer active:scale-[0.98] transition-transform"
      style={{ borderColor: '#E2E8F0', background: 'white' }}
      data-testid={`contrat-card-${contrat.contrat_id}`}
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: '#EFF6FF' }}>
        <FileText size={22} style={{ color: '#3B82F6' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold" style={{ color: '#1e3a5f', fontSize: '1rem' }}>{contrat.numero}</p>
        <p className="text-sm font-semibold truncate" style={{ color: '#374151' }}>
          {contrat.client_nom}
        </p>
        {contrat.validation?.signataire_nom && (
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            Signataire : {contrat.validation.signataire_nom}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ background: '#FFFBEB', color: '#B45309' }}>
          <Clock size={10} className="inline mr-1" />En attente
        </span>
        <ChevronRight size={18} style={{ color: '#CBD5E1' }} />
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function TerrainSignatures() {
  const navigate = useNavigate();
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [signaireNom, setSignaireNom] = useState('');
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getContratsEnAttenteSignature();
      setContrats(res.data || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSignature = (contrat) => {
    setSelected(contrat);
    setSignaireNom(contrat.validation?.signataire_nom || '');
  };

  const handleValidate = async (dataUrl) => {
    if (!selected) return;
    setSigning(true);
    try {
      await api.signerContratTerrain(selected.contrat_id, {
        signataire_nom: signaireNom,
        signature_image_b64: dataUrl,
      });
      toast.success('Signature enregistrée !');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  // Plein écran signature
  if (selected) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#F8FAFF' }}>
        {signing && (
          <div className="absolute inset-0 flex items-center justify-center z-50"
            style={{ background: 'rgba(255,255,255,0.85)' }}>
            <p className="text-lg font-bold" style={{ color: '#1e3a5f' }}>Enregistrement...</p>
          </div>
        )}
        <SignatureCanvas
          contrat={selected}
          signaireNom={signaireNom}
          onChangeSignataire={setSignaireNom}
          onValidate={handleValidate}
          onCancel={() => setSelected(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F0F4FF' }}>
      <div className="flex items-center gap-4 px-6 py-5" style={{ background: 'white', borderBottom: '1px solid #E2E8F0' }}>
        <button onClick={() => navigate('/terrain')}
          className="p-2.5 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={20} style={{ color: '#374151' }} />
        </button>
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#1e3a5f' }}>Contrats à signer</h1>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {contrats.length} contrat{contrats.length !== 1 ? 's' : ''} en attente
          </p>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p style={{ color: '#9CA3AF' }}>Chargement...</p>
          </div>
        ) : contrats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <PenLine size={28} style={{ color: '#3B82F6' }} />
            </div>
            <p className="text-base font-semibold" style={{ color: '#374151' }}>Aucun contrat en attente</p>
            <p className="text-sm text-center" style={{ color: '#9CA3AF' }}>
              Les contrats de location envoyés en signature apparaîtront ici
            </p>
          </div>
        ) : (
          contrats.map(c => (
            <ContratCard key={c.contrat_id} contrat={c} onSign={() => openSignature(c)} />
          ))
        )}
      </div>

      <div className="px-6 py-4 text-center" style={{ borderTop: '1px solid #E2E8F0', background: 'white' }}>
        <button onClick={load} className="text-sm font-medium" style={{ color: '#3B82F6' }}>Actualiser</button>
      </div>
    </div>
  );
}
